import { useEffect, useMemo, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import {
  Play, Pause, Square, Volume2, VolumeX, Copy, Check,
  QrCode, X, Code2, AlertCircle,
} from 'lucide-react';
import QRCode from 'qrcode';

import { BytebeatLayout } from '@/components/BytebeatLayout';
import { WaveformVisualizer } from '@/components/WaveformVisualizer';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

import {
  deserializeFragment,
  detectVariables,
  buildArgs,
  BytebeatFragment,
} from '@/lib/bytebeatFragment';
import { generateVerificationPhrase } from '@/lib/verificationPhrase';
import { generateCoverDataUrl } from '@/lib/coverImage';
import { useBytebeatEngine } from '@/hooks/useBytebeatEngine';

export default function PlayerPage() {
  const location = useLocation();
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [showQr, setShowQr] = useState(false);
  const [copied, setCopied] = useState(false);
  const [userArgValues, setUserArgValues] = useState<Record<string, string>>({});
  const [argsSubmitted, setArgsSubmitted] = useState(false);

  // Parse fragment
  const fragment: BytebeatFragment | null = useMemo(() => {
    return deserializeFragment(location.hash);
  }, [location.hash]);

  const fragmentBase64 = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash;

  const variables = useMemo(() => {
    if (!fragment) return [];
    return detectVariables(fragment.code);
  }, [fragment]);

  // Check which variables are provided vs missing
  const providedArgs = useMemo(() => {
    if (!fragment) return {};
    return buildArgs(variables, fragment.argValues);
  }, [fragment, variables]);

  const missingVars = useMemo(() => {
    return variables.filter((v) => !(v in providedArgs));
  }, [variables, providedArgs]);

  const needsUserInput = missingVars.length > 0 && !argsSubmitted;

  const resolvedArgs = useMemo(() => {
    if (!argsSubmitted) return providedArgs;
    const extra: Record<string, number> = {};
    missingVars.forEach((v) => {
      const raw = userArgValues[v];
      const n = parseFloat(raw ?? '0');
      extra[v] = isNaN(n) ? 0 : n;
    });
    return { ...providedArgs, ...extra };
  }, [providedArgs, missingVars, userArgValues, argsSubmitted]);

  const { isPlaying, play, pause, stop, setVolume, volume, error } = useBytebeatEngine({
    code: fragment?.code ?? '',
    bitrate: fragment?.bitrate ?? 8000,
    args: resolvedArgs,
  });

  // Verification phrase
  const verificationPhrase = useMemo(() => {
    return generateVerificationPhrase(fragmentBase64);
  }, [fragmentBase64]);

  // Cover image
  const coverDataUrl = useMemo(() => {
    if (!fragment) return null;
    try {
      return generateCoverDataUrl(fragment.code, fragment.name);
    } catch {
      return null;
    }
  }, [fragment]);

  // Generate QR
  useEffect(() => {
    if (!showQr) return;
    const url = window.location.href;
    QRCode.toDataURL(url, {
      width: 300,
      margin: 2,
      color: { dark: '#00e6ff', light: '#0f0f1a' },
    }).then(setQrDataUrl).catch(() => {});
  }, [showQr]);

  const currentUrl = window.location.origin + window.location.pathname + location.hash;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  useSeoMeta({
    title: fragment?.name ? `${fragment.name} — Bytebeat Player` : 'Bytebeat Player',
    description: fragment ? `Listen to "${fragment.name}" — a bytebeat composition.` : 'Play bytebeat music.',
  });

  if (!fragment) {
    return (
      <BytebeatLayout>
        <div className="flex flex-col items-center justify-center py-24 text-center gap-6">
          <div className="w-16 h-16 rounded-full bg-muted/50 border border-border flex items-center justify-center">
            <Music2Icon className="w-8 h-8 text-muted-foreground" />
          </div>
          <div>
            <h2 className="text-xl font-semibold mb-2">No Bytebeat Loaded</h2>
            <p className="text-muted-foreground max-w-md">
              The player needs a bytebeat link. Open a shared link, or create your own in the Editor.
            </p>
          </div>
          <Link to="/editor">
            <Button variant="outline" className="gap-2">
              <Code2 className="w-4 h-4" />
              Open Editor
            </Button>
          </Link>
        </div>
      </BytebeatLayout>
    );
  }

  return (
    <BytebeatLayout>
      <div className="space-y-6 max-w-2xl mx-auto">
        {/* Cover + title */}
        <div className="flex gap-5 items-start">
          <div className="relative w-32 h-32 flex-shrink-0 rounded-xl overflow-hidden border border-border/50 box-glow-cyan scanlines isolate">
            {coverDataUrl ? (
              <img src={coverDataUrl} alt={fragment.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-muted flex items-center justify-center">
                <Music2Icon className="w-10 h-10 text-muted-foreground" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0 pt-1">
            <h1 className="text-2xl font-bold truncate glow-cyan text-primary leading-tight mb-1">
              {fragment.name}
            </h1>
            <div className="flex flex-wrap gap-2 mb-3">
              <Badge variant="outline" className="border-primary/30 text-primary/80 text-xs">
                {fragment.bitrate} Hz
              </Badge>
              {Object.keys(resolvedArgs).length > 0 && (
                <Badge variant="outline" className="border-accent/30 text-accent/80 text-xs">
                  {Object.keys(resolvedArgs).length} arg{Object.keys(resolvedArgs).length !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            {/* Verification phrase */}
            <div className="text-xs text-muted-foreground/70 font-mono bg-muted/30 rounded-md px-2 py-1.5 border border-border/30">
              <span className="text-muted-foreground/40 mr-1.5">verify:</span>
              <span className="text-accent/80">{verificationPhrase}</span>
            </div>
          </div>
        </div>

        {/* Variable input prompt */}
        {needsUserInput && (
          <Card className="border-amber-500/30 bg-amber-500/5">
            <CardContent className="pt-4 space-y-4">
              <div className="flex items-center gap-2 text-amber-400">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm font-medium">This bytebeat requires variable values</span>
              </div>
              <div className="grid gap-3">
                {missingVars.map((v) => (
                  <div key={v} className="flex items-center gap-3">
                    <Label className="w-16 font-mono text-sm text-foreground/80 flex-shrink-0">{v}</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={userArgValues[v] ?? ''}
                      onChange={(e) => setUserArgValues((prev) => ({ ...prev, [v]: e.target.value }))}
                      className="bg-background/60 border-border/60 font-mono"
                    />
                  </div>
                ))}
              </div>
              <Button
                size="sm"
                onClick={() => setArgsSubmitted(true)}
                className="w-full bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-300"
              >
                Apply Values
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Error display */}
        {error && (
          <Card className="border-destructive/30 bg-destructive/5">
            <CardContent className="pt-4">
              <div className="flex items-start gap-2 text-destructive">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">Playback Error</p>
                  <p className="text-xs font-mono mt-1 text-destructive/80">{error}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Waveform */}
        <div className="relative rounded-xl overflow-hidden border border-border/50 bg-[#0f0f1a] h-28 scanlines isolate">
          <WaveformVisualizer
            code={fragment.code}
            args={resolvedArgs}
            isPlaying={isPlaying}
          />
        </div>

        {/* Controls */}
        <Card className="border-border/40 bg-card/60">
          <CardContent className="pt-5 space-y-5">
            {/* Play controls */}
            <div className="flex items-center justify-center gap-3">
              <Button
                variant="outline"
                size="icon"
                onClick={stop}
                disabled={!fragment || needsUserInput}
                className="border-border/50 hover:border-primary/40"
                title="Stop"
              >
                <Square className="w-4 h-4" />
              </Button>

              <Button
                size="icon"
                onClick={isPlaying ? pause : play}
                disabled={!fragment || needsUserInput}
                className={
                  isPlaying
                    ? 'w-14 h-14 bg-primary/20 hover:bg-primary/30 border border-primary/60 text-primary animate-pulse-glow'
                    : 'w-14 h-14 bg-primary hover:bg-primary/90 text-primary-foreground'
                }
                title={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
              </Button>

              <Button
                variant="outline"
                size="icon"
                onClick={() => setVolume(volume > 0 ? 0 : 0.8)}
                className="border-border/50 hover:border-primary/40"
                title={volume === 0 ? 'Unmute' : 'Mute'}
              >
                {volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </Button>
            </div>

            {/* Volume slider */}
            <div className="flex items-center gap-3">
              <VolumeX className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              <Slider
                min={0}
                max={1}
                step={0.01}
                value={[volume]}
                onValueChange={([v]) => setVolume(v)}
                className="flex-1"
              />
              <Volume2 className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        {/* Code preview */}
        <div className="rounded-xl border border-border/40 bg-card/40 overflow-hidden">
          <div className="px-4 py-2 border-b border-border/30 flex items-center gap-2">
            <Code2 className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground font-medium">Bytebeat Expression</span>
          </div>
          <pre className="px-4 py-3 text-sm font-mono text-primary/90 whitespace-pre-wrap break-all leading-relaxed">
            {fragment.code}
          </pre>
        </div>

        {/* Share controls */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1 gap-2 border-border/50 hover:border-primary/40"
            onClick={handleCopy}
          >
            {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Copy Link'}
          </Button>

          <Button
            variant="outline"
            className="gap-2 border-border/50 hover:border-accent/40"
            onClick={() => setShowQr(!showQr)}
          >
            {showQr ? <X className="w-4 h-4" /> : <QrCode className="w-4 h-4" />}
            QR Code
          </Button>
        </div>

        {/* QR code display */}
        {showQr && (
          <div className="flex justify-center">
            <div className="inline-block p-4 rounded-xl border border-accent/30 bg-[#0f0f1a] box-glow-purple">
              {qrDataUrl ? (
                <img src={qrDataUrl} alt="QR Code" className="w-52 h-52 rounded-lg" />
              ) : (
                <div className="w-52 h-52 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              <p className="text-center text-xs text-muted-foreground mt-2">Scan to open this bytebeat</p>
            </div>
          </div>
        )}
      </div>
    </BytebeatLayout>
  );
}

function Music2Icon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="18" r="4" />
      <path d="M12 18V2l7 4" />
    </svg>
  );
}
